import React, { useState, useEffect } from "react";
import "./ourFocus.scss";
import style from "../../../../FormFiveLightTheme/Form/formfivelighttheme.module.scss";
import Modal from "react-bootstrap/Modal";
import { blackClose, modalCloseIcon } from "../../../../../images";
import AutoCompleteField from "../../../../../components/AutoCompleteField/AutoCompleteField";
import AutoCompleteOthers from "../../../../../components/AutoCompleteOthers/AutoCompleteOthers";
import CheckboxButton from "../../../../../components/CheckboxButton/CheckboxButton";
import http from "../../../../../helpers/http";
import config from "../../../../../config/config";
import { useBusinessContext } from "../../../../../context/BusinessAccount/BusinessAccountState";
import { useWindowSize } from "react-use";
import useTheme from "../../../../../components/useTheme/useTheme";
import _ from "lodash";
import { Popper } from "@mui/material";

const OurFocus = ({ show, handleClose, data, globalData, workflowQuestions }) => {
  const { theme } = useTheme();
  const { width } = useWindowSize();
  const [formError, setFormError] = useState({});
  const BusinessAccountContext = useBusinessContext();
  const base_url = config.api_url;

  const [formData, setFormData] = useState({
    renovation_work: "",
    project_locations: "",
    project_size_unit: "sq.ft.",
  });

  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [sizesData, setSizesData] = useState([]);
  const [keywordsData, setKeywordsData] = useState([]);
  const [autoKey, setAutoKey] = useState(1);
  const [autoKeyKeywords, setAutoKeyKeywords] = useState(1);

  // Initialize state from data prop when modal opens
  useEffect(() => {
    if (!show) return;

    setFormError({});

    const initial = {
      renovation_work:
        data?.renovation_work === true || data?.renovation_work === "true"
          ? "Yes"
          : data?.renovation_work === false || data?.renovation_work === "false"
          ? "No"
          : "",
      project_locations: data?.project_location?.data || "",
      project_sizes: data?.project_sizes?.sizes || [],
      project_size_unit: data?.project_sizes?.unit || "sq.ft.",
      product_positionings: data?.product_positionings || [],
    };

    setInitialData(initial);

    setFormData({
      renovation_work: initial.renovation_work,
      project_locations: initial.project_locations,
      project_size_unit: initial.project_size_unit,
    });

    setSelectedSizes([]);
    setSelectedKeywords([]);

    // Setup sizes data from globalData
    if (globalData?.project_sizes) {
      const sizes = globalData.project_sizes.map((it) => ({
        label: `${it.size} ${it.unit}`,
        value: it.size,
        unit: it.unit,
      }));
      setSizesData(sizes);
    }

    // Fetch keywords data
    const fetchKeywords = async () => {
      try {
        const { data: apiData } = await http.get(
          `${base_url}/business/options?qs=product_positionings`
        );
        if (apiData) {
          apiData.sort((a, b) => (a.value > b.value ? 1 : -1));
          const keywords = apiData.map((it) => ({
            label: it.label || it.value,
            value: it.value,
          }));
          setKeywordsData(keywords);
        }
      } catch (err) {
        setKeywordsData([]);
      }
    };
    fetchKeywords();
  }, [data, show, globalData]);

  // Disable initial items in dropdowns
  useEffect(() => {
    if (!initialData) return;

    // Disable initial sizes
    if (sizesData.length > 0 && initialData.project_sizes?.length > 0) {
      const updatedSizes = [...sizesData];
      let needsUpdate = false;
      initialData.project_sizes.forEach((size) => {
        const index = _.findIndex(updatedSizes, { value: size });
        if (index !== -1 && !updatedSizes[index].disabled) {
          updatedSizes[index] = { ...updatedSizes[index], disabled: true };
          needsUpdate = true;
        }
      });
      if (needsUpdate) setSizesData(updatedSizes);
    }

    // Disable initial keywords
    if (keywordsData.length > 0 && initialData.product_positionings?.length > 0) {
      const updatedKeywords = [...keywordsData];
      let needsUpdate = false;
      initialData.product_positionings.forEach((kw) => {
        const index = updatedKeywords.findIndex(
          (opt) => opt.value.toLowerCase() === kw.toLowerCase()
        );
        if (index !== -1 && !updatedKeywords[index].disabled) {
          updatedKeywords[index] = { ...updatedKeywords[index], disabled: true };
          needsUpdate = true;
        }
      });
      if (needsUpdate) setKeywordsData(updatedKeywords);
    }
  }, [initialData, keywordsData.length, sizesData.length]);

  const filteredSizes = sizesData.filter(
    (size) => size.unit === formData.project_size_unit
  );

  const handleSelectChange = (value, field) => {
    if (field === "project_size_unit" && value !== formData.project_size_unit) {
      setSelectedSizes([]);
      setSizesData((prev) =>
        prev.map((option) => {
          const isInInitialData = initialData?.project_sizes?.includes(option.value);
          return { ...option, disabled: isInInitialData };
        })
      );
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --- Size handlers ---
  const handleSizeOptionChange = (value) => {
    const selectedIndex = _.findIndex(sizesData, { value });
    if (selectedIndex === -1) return;
    if (sizesData[selectedIndex].unit !== formData.project_size_unit) return;

    const updatedOptions = [...sizesData];
    updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: true };
    setSizesData(updatedOptions);

    setSelectedSizes((prev) => [...prev, value]);
  };

  const handleSizeCheckboxChange = (index, value) => {
    const selectedIndex = _.findIndex(sizesData, { value });
    if (selectedIndex !== -1) {
      const updatedOptions = [...sizesData];
      updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: false };
      setSizesData(updatedOptions);
    }

    setSelectedSizes((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSizeRemove = (value) => {
    const selectedIndex = _.findIndex(sizesData, { value });
    if (selectedIndex !== -1) {
      const updatedOptions = [...sizesData];
      updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: false };
      setSizesData(updatedOptions);
    }

    setInitialData((prev) => ({
      ...prev,
      project_sizes: prev.project_sizes.filter((item) => item !== value),
    }));
  };

  // --- Keyword handlers ---
  const handleKeywordSelect = (value) => {
    // Disable in dropdown
    const selectedIndex = keywordsData.findIndex(
      (opt) => opt.value.toLowerCase() === value.toLowerCase()
    );
    if (selectedIndex !== -1) {
      const updatedOptions = [...keywordsData];
      updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: true };
      setKeywordsData(updatedOptions);
    }

    setSelectedKeywords((prev) => [...prev, value]);
  };

  const handleKeywordCheckboxChange = (index, value) => {
    // Re-enable in dropdown
    const selectedIndex = keywordsData.findIndex(
      (opt) => opt.value.toLowerCase() === value.toLowerCase()
    );
    if (selectedIndex !== -1) {
      const updatedOptions = [...keywordsData];
      updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: false };
      setKeywordsData(updatedOptions);
    }

    setSelectedKeywords((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleKeywordRemove = (value) => {
    // Re-enable in dropdown
    const selectedIndex = keywordsData.findIndex(
      (opt) => opt.value.toLowerCase() === value.toLowerCase()
    );
    if (selectedIndex !== -1) {
      const updatedOptions = [...keywordsData];
      updatedOptions[selectedIndex] = { ...updatedOptions[selectedIndex], disabled: false };
      setKeywordsData(updatedOptions);
    }

    setInitialData((prev) => ({
      ...prev,
      product_positionings: prev.product_positionings.filter(
        (item) => item.toLowerCase() !== value.toLowerCase()
      ),
    }));
  };

  const shouldRenderField = (fieldKey) => {
    if (!workflowQuestions || !data?.business_types) {
      return false;
    }

    const question = workflowQuestions.find((q) => q.question === fieldKey);
    if (!question) {
      return false;
    }

    const userBusinessTypeIds = data.business_types.map((bt) => bt._id);
    const questionBusinessTypeIds = question.business_types.map((bt) => bt._id);

    return userBusinessTypeIds.some((id) =>
      questionBusinessTypeIds.includes(id)
    );
  };

  const handleSubmit = async () => {
    setFormError({});

    const allKeywords = [
      ...(initialData?.product_positionings || []),
      ...selectedKeywords,
    ];

    try {
      const payload = {
        product_positionings: allKeywords,
        renovation_work: formData.renovation_work === "Yes",
        project_location: { data: formData.project_locations },
        project_sizes: {
          sizes: [
            ...(initialData?.project_size_unit === formData.project_size_unit
              ? [...(initialData.project_sizes || []), ...selectedSizes]
              : [...selectedSizes]),
          ],
          unit: formData.project_size_unit,
        },
      };

      const response = await http.post(
        `${base_url}/business/business-details/${BusinessAccountContext?.data?._id}`,
        payload
      );

      if (response.data) {
        BusinessAccountContext.update({
          ...BusinessAccountContext.data,
          ...payload,
        });
        window.dispatchEvent(new Event("saveSuccess"));
        handleClose();
      }
    } catch (err) {
      setFormError({
        submit: "Failed to save. Please try again.",
      });
    }
  };

  const handleModalClose = () => {
    setFormData({
      renovation_work: initialData?.renovation_work || "",
      project_locations: initialData?.project_locations || "",
      project_size_unit: initialData?.project_size_unit || "sq.ft.",
    });
    setSelectedSizes([]);
    setSelectedKeywords([]);
    handleClose();
  };

  const renderKeywordsSection = () => {
    const selectedList = selectedKeywords.map((keyword, i) => (
      <CheckboxButton
        lightTheme={theme === "dark" ? false : true}
        isChecked={true}
        label={keyword}
        labelId={keyword}
        onChange={() => handleKeywordCheckboxChange(i, keyword)}
        key={`kw-${i}`}
      />
    ));

    return (
      <div className="our_focus_chips_wrapper">
        <div className={`${style.steps} ${style.fastep04}`}>
          <div className={`${style.add_more_wrap} ${style.add_more_v2}`}>
            <div className={`${style.field_wrapper} field_wrapper`}>
              <ul className="checkboxes chips_b_edit_autoComplete">
                {initialData?.product_positionings?.map((value, index) => (
                  <li
                    className="black_border_chips_custom"
                    key={`kw-chip-${index}`}
                    onClick={() => handleKeywordRemove(value)}
                  >
                    {value}
                    <img
                      src={blackClose}
                      alt="close"
                      className="close_icon"
                    />
                  </li>
                ))}
                {selectedList}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectSizesSection = () => {
    const selectedList = selectedSizes.map((option, i) => (
      <CheckboxButton
        lightTheme={theme === "dark" ? false : true}
        isChecked={true}
        label={`${option} ${formData.project_size_unit}`}
        labelId={option}
        onChange={() => handleSizeCheckboxChange(i, option)}
        key={`size-${i}`}
      />
    ));

    return (
      <div className="our_focus_chips_wrapper">
        <div className={`${style.steps} ${style.fastep04}`}>
          <div className={`${style.add_more_wrap} ${style.add_more_v2}`}>
            <div className={`${style.field_wrapper} field_wrapper`}>
              <ul className="checkboxes chips_b_edit_autoComplete">
                {formData.project_size_unit ===
                  initialData?.project_size_unit &&
                  initialData?.project_sizes?.map((value, index) => (
                    <li
                      className="black_border_chips_custom"
                      key={`size-chip-${index}`}
                      onClick={() => handleSizeRemove(value)}
                    >
                      {value} {data?.project_sizes?.unit}
                      <img
                        src={blackClose}
                        alt="close"
                        className="close_icon"
                      />
                    </li>
                  ))}
                {selectedList}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      className={`our_focus_popup our_focus--modal ${
        theme === "dark" && "our_focus--modal-dark-mode"
      }`}
      backdropClassName={`custom-backdrop ${
        theme === "dark" && "our-focus-custom-backdrop--modal-dark-mode"
      }`}
    >
      <Modal.Header>
        <button className="custom-cancel-btn" onClick={handleModalClose}>
          <img
            src={modalCloseIcon}
            alt="close-icon"
            className="ctm_img_bussine_edit_about"
          />
        </button>
      </Modal.Header>
      <Modal.Body>
        <h2 className="about_modal_bussines_heading fw-semibold">Our Focus</h2>
        <p className="ai_matchmaking_info_text">This information is used by Archinza AI<sup>™</sup> to matchmake your ideal clients.</p>
        <div className="our_focus_select_container">
          {/* Renovation Work */}
          {shouldRenderField("renovation_work") && (
            <div className="our_focus_field_wrapper">
              <AutoCompleteField
                lightTheme={theme === "dark" ? false : true}
                key="renovation_work"
                textLabel="Do you do renovation work?"
                data={[
                  { label: "Yes", value: "Yes" },
                  { label: "No", value: "No" },
                ]}
                value={
                  formData.renovation_work
                    ? [
                        { label: "Yes", value: "Yes" },
                        { label: "No", value: "No" },
                      ].find(
                        (option) => option.value === formData.renovation_work
                      ) || null
                    : null
                }
                onChange={(e, option) =>
                  handleSelectChange(option?.value || "", "renovation_work")
                }
              />
            </div>
          )}

          {/* Market Positioning Keywords */}
          <div className="our_focus_field_wrapper">
            <AutoCompleteOthers
              lightTheme={theme === "dark" ? false : true}
              key={`autokey${autoKeyKeywords}-keywords`}
              textLabel="Pick words that apply to your market positioning"
              data={keywordsData}
              onChange={(e, value) => {
                handleKeywordSelect(value.value);
                setAutoKeyKeywords((prev) => prev + 1);
              }}
              allowAddOption={true}
              hintText="Type a different option"
              addButtonLabel="Add"
            />
          </div>
          {renderKeywordsSection()}

          {/* Project/Client Location Preference */}
          {shouldRenderField("location_preference") && (
            <div className="our_focus_field_wrapper">
              <AutoCompleteField
                lightTheme={theme === "dark" ? false : true}
                key="project_locations"
                textLabel="Project/Client location preference"
                data={
                  globalData?.project_locations?.map((it) => ({
                    label: it,
                    value: it,
                  })) || []
                }
                value={
                  globalData?.project_locations
                    ?.map((it) => ({
                      label: it,
                      value: it,
                    }))
                    .find(
                      (option) => option.value === formData.project_locations
                    ) || null
                }
                onChange={(e, option) =>
                  handleSelectChange(option?.value || "", "project_locations")
                }
              />
            </div>
          )}

          {/* Minimum Project Size */}
          {shouldRenderField("minimum_project_size") && (
            <>
              <div className="our_focus_2field_wrapper">
                <div className="our_focus_field_wrapper mb-0">
                  <AutoCompleteOthers
                    lightTheme={theme === "dark" ? false : true}
                    key={`autokey${autoKey}-project_sizes`}
                    textLabel="Minimum Project Size"
                    data={filteredSizes}
                    onChange={(e, value) => {
                      handleSizeOptionChange(value.value);
                      setAutoKey((prev) => prev + 1);
                    }}
                    allowAddOption={false}
                  />
                </div>
                <div className="our_focus_field_wrapper mb-0">
                  <AutoCompleteField
                    lightTheme={theme === "dark" ? false : true}
                    key="project_size_unit"
                    textLabel="Unit"
                    data={
                      _.uniqBy(globalData?.project_sizes, "unit")?.map(
                        (item) => ({
                          label: item.unit,
                          value: item.unit,
                        })
                      ) || []
                    }
                    value={
                      _.uniqBy(globalData?.project_sizes, "unit")
                        ?.map((item) => ({
                          label: item.unit,
                          value: item.unit,
                        }))
                        .find(
                          (option) =>
                            option.value === formData.project_size_unit
                        ) || null
                    }
                    onChange={(e, option) =>
                      handleSelectChange(
                        option?.value || "",
                        "project_size_unit"
                      )
                    }
                    className="modal_autocomplete--our-focus"
                    PopperComponent={(props) => (
                      <Popper
                        {...props}
                        container={document.querySelector(
                          ".modal_autocomplete--our-focus"
                        )}
                      />
                    )}
                  />
                </div>
              </div>
              {renderProjectSizesSection()}
            </>
          )}

          {formError.submit && (
            <p
              className="error_text"
              style={{ color: "red", marginTop: "10px" }}
            >
              {formError.submit}
            </p>
          )}

          <div className="field_wrapper">
            <button
              type="button"
              className="popup_common_btn"
              onClick={handleSubmit}
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OurFocus;
